import { CalListCache } from "../server/EventStore.ts"
import * as FwDatas from "../FwDatas.ts"
import * as i18n from "../i18n.ts"
import { format, getMonthRange, getYearRangeFrom } from "../lib/DateUtils.ts";
import { ContactCache } from "../contact/ContactCache.ts";
import { BaseCache, ListCache, TranslationCache } from "../lib/SyncCache.ts";
import { TaskListCache } from "../server/objects/TaskBeans.ts";
import IHasRecurrencyHelper from "../lib/IHasRecurrencyHelper.ts";
import { createFieldSearcher } from "../widget/searchController.ts";
import * as amplify from "../util/amplify.ts";
import { IEvent, RecurrencyDescriptor, TaskListBean } from "../server/fizapilib.auto.ts";


type SpecialDaysFolder = {
  name:string,
  month?:number,
  events : SpecialDaysEventView[]
}
type SpecialDaysView = {
  items:SpecialDaysFolder[]
  getDefaultItem:()=>SpecialDaysEventView
  find:(metaId : string)=>SpecialDaysEventView
}
type SpecialDaysEventView = {
  metaId:string
  start:Date
  startDate:Date
  title:string
  sub:string
  deltaDays :number
  type:string
  pictureUrl?:string,
  emoji?:string,
  color:string,
  displayDate:string
  iEvent:IEvent
  masterEvent:IEvent
  recurrencyDescriptor:RecurrencyDescriptor,
  taskList?:TaskListBean
}
export function getEmojiByType(eventType : string){
  switch(eventType){
    case "SPECIAL_DAYS":
      return "🎉"
    case "ANNIVERSARY":
      return "💌"
    default:{
      return "🎂"
    }
  }
}

var currentFamilyIdLong = undefined;
export function getSpecialDaysCalendarId(){
  return "calendarSpecialDays/"+(currentFamilyIdLong!==undefined?currentFamilyIdLong:FwDatas.getFamily().family_id)
}

const CACHES : {
  SpecialDayCache?:ListCache<IEvent>,
  SpecialDayInstanceCache?:ListCache<SpecialDaysEventView>
} = {};

amplify.subscribe("familyScopeChange",(familyMetaId)=>{
  currentFamilyIdLong = parseInt(familyMetaId.substring(familyMetaId.indexOf('/')+1))
  CACHES.SpecialDayCache = undefined
  CACHES.SpecialDayInstanceCache = undefined
})
amplify.subscribe("eventCreated contactCreated eventUpdated contactUpdated memberUpdated eventDeleted contactDeleted",(familyMetaId)=>{
  CACHES.SpecialDayCache?.invalidate()
  CACHES.SpecialDayInstanceCache?.invalidate()
})

export function getSpecialdaysCache(){
  if(!CACHES.SpecialDayCache){

    CACHES.SpecialDayCache = CalListCache.getCache(getSpecialDaysCalendarId())
    CACHES.SpecialDayInstanceCache = new ListCache<SpecialDaysEventView>(
      async ()=>{
        await CACHES.SpecialDayCache.refresh();
        var
          result = getNextYear(CACHES.SpecialDayCache);
        result.sort((a,b)=>a.start.getTime()-b.start.getTime())
        return result;
      }
    )
  }
  return CACHES
}

type SpecialDaysEvent = IEvent & {
  deltaDays:number
  labelDays:string
  type : string
  displayDate : string
}
export function translateEvent(event :IEvent){
  function proto(this:SpecialDaysEvent){
      this.deltaDays = Math.ceil((event.start.getTime()-new Date().getTime())/(3600000*24));
      this.labelDays = this.deltaDays>1?"timeunit.days":"timeunit.day";

      this.type = "SPECIAL_DAYS";
      this.emoji = event.emoji||getEmojiByType(event.eventType)
      this.pictureUrl = event.medias[0]?.pictureUrl || event.pictureUrl;
      if(event.allDay){
        this.displayDate = event.birthDateNoYear?format(event.start,'DM'):format(event.start,'LL')
      }else{
        this.displayDate = format(event.start,'LLL')
      }
      this.taskList = event.taskListId===undefined?undefined:TaskListCache.get(event.taskListId)
  }
  proto.prototype = event;
  var translated =  new proto()


  const TRANSLATION_BY_TYPE = {
    commonbirthday(){
      if(event.birthDateNoYear){
        translated.sub = i18n.get('specialdays.birthday')
      }else{
        translated.sub = i18n.get('specialdays.birthday')+" • "+i18n.get('specialdays.birthdayN',event.occurenceIndex)
      }
      translated.type = i18n.get('specialdays.birthday')
      if(event.birthDateNoYear){
        translated.countdown = i18n.get('specialdays.countdownNoYear')
      }else{
        translated.countdown = i18n.get('specialdays.countdown',i18n.get('specialdays.birthdayN',event.occurenceIndex))
      }

    },
    BIRTHDAY(){
      this.commonbirthday()
      translated.title = ContactCache.get(event.refPersonId).displayName,
      translated.pictureUrl = ContactCache.get(event.refPersonId)?.thumb()
    },
    BIRTHDAY_ACCOUNT(){
      this.commonbirthday()
      translated.title = FwDatas.getProfile(parseInt(event.refPersonId)).firstName,
      translated.pictureUrl = FwDatas.getProfile(parseInt(event.refPersonId)).thumb()
    },
    BIRTHDAY_CUSTOM(){
      this.commonbirthday()
      translated.pictureUrl = event.thumb()
    },
    ANNIVERSARY(){
      translated.sub = i18n.get('specialdays.anniversaryN',event.occurenceIndex)
      translated.type = i18n.get('specialdays.anniversary')
      translated.countdown = i18n.get('specialdays.countdown',i18n.get('specialdays.anniversaryN',event.occurenceIndex))
    },
    SPECIAL_DAYS(){
      translated.sub = i18n.get('specialdays.tradition')+" • "+i18n.get('specialdays.traditionN',event.occurenceIndex+1)
      translated.type = i18n.get('specialdays.tradition')
      translated.countdown = i18n.get('specialdays.countdown',i18n.get('specialdays.traditionN',event.occurenceIndex+1))
    },
    UNKNOWN(){

    }
  }[event.eventType]()


  return translated;

}

function getNextYear(rootEventCache: ListCache<ApiServer.types.IEvent>){
  const
    range = getYearRangeFrom();


  var
    instances = [];
  for(var event of rootEventCache.datas){
    var eventInstance =  IHasRecurrencyHelper.getFirstOccurenceAfter(event,range.start)
    if(eventInstance){
      instances.push(translateEvent(eventInstance))
    }
  }
  instances.sort((a,b)=>a.startDate.getTime()-b.startDate.getTime())
  return instances;
}

var specialdaysSearcher = createFieldSearcher<SpecialDaysEventView>("title:1,description:1,where:1")
export async function specialDaysFoldered(instanceCache :BaseCache<SpecialDaysEventView[]>, displayContext:{search:string}){

  const
    [instances] =  await Promise.all([instanceCache.need(),ContactCache.need(),TaskListCache.need()]),
    range = getYearRangeFrom(),
    monthrange = getMonthRange(),
    upcoming :SpecialDaysFolder = {name:"",events : []},
    upcomingMonth = {name:"",events : []},
    upcomingbyMonth : SpecialDaysFolder[] = [],
    currentFullYear =  new Date().getFullYear();



  for(var event of instances){
    if(displayContext.search.length>0){
      if(specialdaysSearcher(event,displayContext.search)===0) continue;
    }
    if(event.start.getTime()<range.start.getTime()+3600000*24*2){
      upcoming.events.push(event)
      continue;
    }
    if(event.start.getTime()<monthrange.end.getTime()){
      upcomingMonth.events.push(event)
      continue;
    }
    if(upcomingbyMonth[upcomingbyMonth.length-1]?.month===event.start.getMonth()){
      upcomingbyMonth[upcomingbyMonth.length-1].events.push(event)
    }else{
      var
        isSameYear = (event.start.getFullYear()===currentFullYear),
        name = isSameYear?format(event.start,'MMMM'):format(event.start,'MMMM')+" "+(event.start.getFullYear());


      upcomingbyMonth.push({name,month:event.start.getMonth(),events:[event]})
    }

  }
  upcoming.name = i18n.get('specialdays.folder.upcoming')+" ("+upcoming.events.length+")"
  upcomingMonth.name = i18n.get('specialdays.folder.later',format(monthrange.start,'MMMM'));

  var items = [upcoming,upcomingMonth,...upcomingbyMonth].filter(folder=>folder.events.length>0);

  items.forEach(folder=>{
    folder.events.sort((a,b)=>{
      return a.start - b.start ||
        a.title.localeCompare(b.title)
    })
  })
  return {
    items,
    getDefaultItem(){
      return this.items[0][0];
    },
    find(metaId : string){
      return this.items.find(folder=>{
        return folder.events.find(item=>item.metaId===metaId)
      })
    }
  }
}

